
import Vue from 'vue';
import {
  SmallDetailsLoader,
  SmallDetailsMultipleLogos,
} from '@/components/SmallDetails';
import { revalidateSmallDetails } from '@/utils/smallDetailsHelperV2';
import SmallDetailsConfirmation from '@/components/SmallDetails/Confirmation/SmallDetailsConfirmation.vue';
import { mapState, mapStores, mapActions } from 'pinia';
import { useStore as useShapeStore } from '@/stores/v2/shape';
import { useStore as useProductStore } from '@/stores/v2/product';
import { useStore as useNotificationStore } from '@/stores/v2/notification';
import { useStore as useGlobalStore } from '@/stores/v2/global';
import { useStore as useLangStore } from '@/stores/v2/lang';
import { useStore as useHistoryStore } from '@/stores/v2/history';
import Reset16 from '@carbon/icons-vue/es/reset/16';
import { Personalization } from '@midocean/webapps-shared-domain';
import {
  isRevalidationNeeded,
  trySavePersonalization,
  triggerSmallDetailsCheck,
} from '@/utils/personalizationHelperV2';
import { DoSmallDetailsPart } from '@/constants/imageProcessingEngine';
import { PdpChoicesSetterType } from '@/setters/typings';
import i18n from '@/i18n';
import { createStateSnapshot } from '@/utils/historyHelperV2';

declare module 'vue/types/vue' {
  interface Vue {
    $pdpChoices: PdpChoicesSetterType;
    $sapCustomerId: string;
    $isEditMode: boolean;
    $personalization: Personalization;
    $isDirectApproval: boolean;
    $customerCompanyLogoUrl: string;
  }
}

export default Vue.extend({
  name: 'ManualCheck',
  components: {
    SmallDetailsLoader,
    SmallDetailsMultipleLogos,
    SmallDetailsConfirmation,
    Reset16,
  },
  inject: [
    '$pdpChoices',
    '$sapCustomerId',
    '$isEditMode',
    '$personalization',
    '$isDirectApproval',
    '$customerCompanyLogoUrl',
  ],
  props: {
    triggerSmallDetailsCheck: {
      type: Boolean,
      required: false,
      default: false,
    },
    isConcept: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      isLoaderOpen: false,
      isSmallDetailsOpen: false,
      isConfirmationOpen: false,
    };
  },
  computed: {
    ...mapStores(useShapeStore, useProductStore, useNotificationStore),
    ...mapState(useShapeStore, ['shapes', 'positionShapeMap', 'activeShapeId']),
    ...mapState(useProductStore, ['data', 'unavailablePrintPositions']),
    ...mapState(useLangStore, ['languageInUse']),
    ...mapState(useNotificationStore, ['notifications']),

    isDisabled(): boolean {
      return (
        this.shapes.filter((x) => x.smallDetailsResult !== undefined).length ===
        0
      );
    },
    checkStatusLabel(): string {
      let worstStatus = '';
      let worstStatusValue = 3;
      this.shapes.forEach((x) => {
        if (x.smallDetailsResult) {
          if (!x.smallDetailsResult.isCheckValid) {
            worstStatus = i18n
              .t(`wizard-v2.small-details.invalid-check`)
              .toString()
              .toUpperCase();
            worstStatusValue = 0;
          }
          if (
            x.smallDetailsResult.isCheckValid &&
            x.smallDetailsResult.status === 'Bad' &&
            worstStatusValue > 0
          ) {
            worstStatus = i18n
              .t(
                `wizard-v2.small-details.${x?.smallDetailsResult.status.toLowerCase()}`
              )
              .toString()
              .toUpperCase();
            worstStatusValue = 1;
          }
          if (
            x.smallDetailsResult.isCheckValid &&
            x.smallDetailsResult.status === 'Average' &&
            worstStatusValue > 1
          ) {
            worstStatus = i18n
              .t(
                `wizard-v2.small-details.${x?.smallDetailsResult.status.toLowerCase()}`
              )
              .toString()
              .toUpperCase();
            worstStatusValue = 2;
          }
          if (
            x.smallDetailsResult.isCheckValid &&
            x.smallDetailsResult.status === 'Good' &&
            worstStatusValue > 2
          ) {
            worstStatus = i18n
              .t(
                `wizard-v2.small-details.${x?.smallDetailsResult.status.toLowerCase()}`
              )
              .toString()
              .toUpperCase();
            worstStatusValue = 3;
          }
        }
      });

      return worstStatus;
    },
  },
  watch: {
    triggerSmallDetailsCheck(newValue) {
      if (newValue) {
        this.onManualCheck();
      }
    },
  },
  methods: {
    ...mapActions(useGlobalStore, [
      'setIsProcessingOrder',
      'setIsLoaderActive',
    ]),
    ...mapActions(useNotificationStore, ['runValidators']),
    ...mapActions(useHistoryStore, ['recordHistory']),

    async onManualCheck() {
      this.$modal.hide('pdf-preview-modal');

      if (isRevalidationNeeded()) {
        this.isLoaderOpen = true;
        await revalidateSmallDetails();
        this.runValidators();
        this.isLoaderOpen = false;

        const snapshot = createStateSnapshot();
        this.recordHistory(snapshot);
      }

      const isConfirmationNeeded =
        this.shapes.filter((x) => x.smallDetailsResult?.status !== 'Good')
          .length > 0;

      if (this.triggerSmallDetailsCheck && isConfirmationNeeded) {
        this.isConfirmationOpen = true;
      } else if (this.triggerSmallDetailsCheck && !isConfirmationNeeded) {
        this.trySavePersonalization();
      } else {
        this.isSmallDetailsOpen = true;
      }
    },

    onUseCurrentDesign() {
      const unAcceptedShapes = this.shapes.filter(
        (x) => x.smallDetailsResult?.status !== 'Good'
      );

      const utcDate = new Date().toISOString();

      unAcceptedShapes.map((x) => {
        if (x.smallDetailsResult) {
          x.smallDetailsResult.approvedAt = utcDate;
        }
      });

      this.trySavePersonalization();

      this.isConfirmationOpen = false;
    },
    async trySavePersonalization() {
      try {
        this.setIsProcessingOrder(true);
        this.setIsLoaderActive(true);
        const personalizationId = await trySavePersonalization(
          this.$isEditMode,
          this.$pdpChoices.productVariants,
          this.$sapCustomerId,
          false,
          this.$isDirectApproval,
          this.$customerCompanyLogoUrl,
          this.languageInUse
        );
        const eventName = this.isConcept
          ? 'wizard:saved-as-concept'
          : 'wizard:ordered';

        this.$emit(eventName, { personalizationId });
        this.$emit('action:disable-pintura');
      } catch (e: unknown) {
        console.log((e as Error).message);
      } finally {
        this.setIsProcessingOrder(false);
        this.setIsLoaderActive(false);
      }
    },
    backToWizard() {
      this.$emit('action:reset-flag');
      this.isSmallDetailsOpen = false;
      this.isConfirmationOpen = false;
    },
  },
});
